import React from 'react';
import { Box, Typography, Button, Paper, Dialog, Backdrop } from '@mui/material';
import { styled } from '@mui/material/styles';

interface NotificationModalProps {
  open: boolean;
  onClose?: () => void; // onClose might be optional if the button is the only way to close
  message?: string;
  buttonText?: string;
  onButtonClick?: () => void; // This could also handle closing the modal
  className?: string;
  blurAmount?: number;
}

// Styled Backdrop for the blur effect (can be shared if used elsewhere)
const StyledBackdrop = styled(Backdrop, {
  shouldForwardProp: (prop) => prop !== 'blurAmount',
})<{ blurAmount?: number }>(({ blurAmount = 4 }) => ({
  backdropFilter: `blur(${blurAmount}px)`,
}));

// Card content styling (similar to previous NotificationCard)
const CardContentContainer = styled(Paper)(({ theme }) => ({
  maxWidth: '319px', // Will be constrained by Dialog's maxWidth
  paddingBottom: '24px', // 24px
  background: '#FFF',
  border: '1px solid var(--gray-100, #E4E7EC)',
  boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.25)',
  // No need for boxShadow here as Dialog's Paper will provide it
  overflow: 'hidden',
  borderRadius: '12px', // Applied to Dialog's PaperProps
  // No need for border here as Dialog's Paper can handle it or it might be undesired for a modal
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

const GradientBar = styled(Box)({
  alignSelf: 'stretch',
  height: '3px',
  background: 'linear-gradient(139deg, #2563EB 0%, #7C3AED 100%)',
});

const MessageContainer = styled(Box)(({ theme }) => ({
  alignSelf: 'stretch',
  padding: theme.spacing(3), // 24px
  justifyContent: 'center',
  gap: theme.spacing(2),
  alignItems: 'center',
  display: 'flex',
}));

const MessageText = styled(Typography)(({ theme }) => ({
  flex: '1 1 0',
  textAlign: 'center',
  color: "var(--gray-700, #111827)",
  fontFamily: 'Inter, sans-serif',
  fontWeight: '400',
  lineHeight: '26px',
  wordWrap: 'break-word',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '120px',
  height: '36px',
  padding: theme.spacing(1.25, 2.375),
  background: 'linear-gradient(139deg, #2563EB 0%, #7C3AED 100%)',
  borderRadius: '20px',
  color: 'white',
  fontSize: '14px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: '700',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(139deg, #1E4FB8 0%, #6B2DBF 100%)',
  },
  marginTop: theme.spacing(1), // Add some space above the button
}));

export const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  onClose, // User can close by clicking backdrop or pressing Esc if provided
  message = "Le prélèvement des documents depuis votre logiciel métier a débuté. Vous recevrez un e-mail lorsque vous pourrez utiliser l'Assistant sur ce sous-dossier.",
  buttonText = "Compris",
  onButtonClick,
  className,
  blurAmount = 4,
}) => {
  const handleInternalButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    }
    // Optionally, always close the modal when the button is clicked,
    // unless onButtonClick is expected to handle it.
    // if (onClose) {
    //   onClose();
    // }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose} // Allows closing by clicking backdrop or Esc
      className={className}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.25)', // Custom shadow from Figma
          border: '1px solid #E4E7EC', // Outline from Figma
          overflow: 'visible', // Allow gradient bar to be flush if DialogContent has padding
          background: 'transparent', // Let CardContentContainer define background
          zIndex: (theme) => theme.zIndex.modal +1,
        },
        elevation: 0, // Disable MUI's default Paper shadow if using custom
      }}
      BackdropComponent={StyledBackdrop}
      BackdropProps={{
        blurAmount: blurAmount,
        sx: { zIndex: (theme) => theme.zIndex.modal }
      }}
      maxWidth="xs" // Keeps it relatively small, adjust as needed (e.g., 'sm')
    >
      {/* No DialogTitle or DialogContent needed if CardContentContainer handles all layout */}
      <CardContentContainer elevation={2}> {/* elevation 0 as PaperProps on Dialog has shadow */}
        <GradientBar />
        <MessageContainer>
          <MessageText>{message}</MessageText>
        </MessageContainer>
        <StyledButton onClick={handleInternalButtonClick}>
          {buttonText}
        </StyledButton>
      </CardContentContainer>
    </Dialog>
  );
}; 